import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, RefreshCw, Boxes, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { dynamicDocTypesApi } from '../services/dynamicDocTypesApi';
import logger from '../utils/logger';
import { genStableUid } from '../hooks/useArrayWithStableKeys';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'select', label: 'Select (dropdown)' },
  { value: 'email', label: 'Email' },
  { value: 'link', label: 'Link (reference)' },
];

const _uid = genStableUid;

const emptyField = () => ({
  _uid: _uid(),
  fieldname: '',
  label: '',
  fieldtype: 'text',
  required: false,
  options: '',           // comma-separated in the form
  link_doctype: '',      // target doctype when fieldtype=link
  default: '',
  in_list_view: false,
  unique: false,
  sort_order: 0,
});

const emptyForm = () => ({
  doctype_name: '',
  label: '',
  module: 'Custom',
  description: '',
  naming_rule: 'autoincrement',
  naming_prefix: '',
  naming_field: '',
  active: true,
  fields: [emptyField()],
});

const DocTypeBuilder = () => {
  const navigate = useNavigate();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dynamicDocTypesApi.list();
      setDefinitions(r.data || []);
    } catch (err) {
      logger.error('Failed to load doctypes', err);
      toast.error(err.response?.data?.detail || 'Failed to load custom DocTypes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    setEditMode(null);
    setFormData(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (def) => {
    setEditMode(def.definition_id);
    setFormData({
      doctype_name: def.doctype_name,
      label: def.label,
      module: def.module || 'Custom',
      description: def.description || '',
      naming_rule: def.naming_rule || 'autoincrement',
      naming_prefix: def.naming_prefix || '',
      naming_field: def.naming_field || '',
      active: def.active !== false,
      fields: (def.fields || []).map(f => ({
        ...emptyField(),
        ...f,
        _uid: f._uid || _uid(),
        options: Array.isArray(f.options) ? f.options.join(', ') : '',
        default: f.default ?? '',
      })),
    });
    setDialogOpen(true);
  };

  const handleFieldChange = (uid, patch) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => (f._uid === uid ? { ...f, ...patch } : f)),
    }));
  };

  const addField = () => {
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, emptyField()],
    }));
  };

  const removeField = (uid) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f._uid !== uid),
    }));
  };

  const buildPayload = () => {
    const payload = {
      doctype_name: formData.doctype_name.trim().toLowerCase(),
      label: formData.label.trim(),
      module: formData.module.trim() || 'Custom',
      description: formData.description.trim() || null,
      naming_rule: formData.naming_rule,
      naming_prefix: formData.naming_prefix.trim() || null,
      naming_field: formData.naming_field.trim() || null,
      active: formData.active,
      fields: formData.fields
        .filter(f => f.fieldname.trim() && f.label.trim())
        .map(f => ({
          fieldname: f.fieldname.trim().toLowerCase(),
          label: f.label.trim(),
          fieldtype: f.fieldtype,
          required: !!f.required,
          options: f.fieldtype === 'select'
            ? f.options.split(',').map(o => o.trim()).filter(Boolean)
            : null,
          link_doctype: f.fieldtype === 'link' ? (f.link_doctype || null) : null,
          default: f.default === '' ? null : f.default,
          in_list_view: !!f.in_list_view,
          unique: !!f.unique,
          sort_order: Number(f.sort_order) || 0,
        })),
    };
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload.doctype_name) { toast.error('DocType name is required'); return; }
    if (!payload.label) { toast.error('Label is required'); return; }
    if (payload.fields.length === 0) { toast.error('Add at least one field'); return; }
    if (payload.naming_rule === 'field' && !payload.naming_field) {
      toast.error('naming_field is required when naming_rule=field');
      return;
    }
    const linkMissingTarget = payload.fields.find(f => f.fieldtype === 'link' && !f.link_doctype);
    if (linkMissingTarget) {
      toast.error(`Link field '${linkMissingTarget.fieldname}' requires a target DocType`);
      return;
    }

    setSaving(true);
    try {
      if (editMode) {
        const { doctype_name, ...updatePayload } = payload;  // name is immutable
        await dynamicDocTypesApi.update(editMode, updatePayload);
        toast.success('DocType updated');
      } else {
        await dynamicDocTypesApi.create(payload);
        toast.success('DocType created');
      }
      setDialogOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (def) => {
    const dropData = window.confirm(
      `Delete DocType "${def.label}"?\n\n` +
      `Click OK to ALSO DROP all data records.\n` +
      `Click Cancel to keep records (definition only removed).`,
    );
    if (dropData === null) return;  // rare; guard anyway
    try {
      await dynamicDocTypesApi.delete(def.definition_id, { dropData });
      toast.success('DocType deleted');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="doctype-builder-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Boxes className="w-6 h-6 text-stone-700" /> Custom DocType Builder
            </h1>
            <p className="text-gray-500 text-sm">
              Define your own business objects without writing code. Each DocType gets
              its own REST API and list/form UI automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={openCreate}
              className="bg-stone-800 hover:bg-stone-700"
              data-testid="new-doctype-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New DocType
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Defined DocTypes ({definitions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading…</div>
            ) : definitions.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <Boxes className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No custom DocTypes yet. Create one to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Fields</TableHead>
                    <TableHead>Naming</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {definitions.map(def => (
                    <TableRow key={def.definition_id} data-testid={`doctype-row-${def.doctype_name}`}>
                      <TableCell className="font-mono text-sm">{def.doctype_name}</TableCell>
                      <TableCell className="font-medium">{def.label}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{def.module}</Badge>
                      </TableCell>
                      <TableCell>{(def.fields || []).length}</TableCell>
                      <TableCell className="text-xs">
                        {def.naming_rule === 'autoincrement'
                          ? `${(def.naming_prefix || def.doctype_name.slice(0, 4).toUpperCase())}-#####`
                          : `field: ${def.naming_field}`}
                      </TableCell>
                      <TableCell>
                        <Badge className={def.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}>
                          {def.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/doc/${def.doctype_name}`)}
                            data-testid={`open-doctype-${def.doctype_name}`}
                          >
                            Open <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(def)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(def)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* --- Builder Dialog --- */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit DocType' : 'Create DocType'}</DialogTitle>
              <DialogDescription>
                Define a new business object. Fields become form inputs, list columns, and REST endpoints automatically.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              {/* Basics */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Technical Name * <span className="text-xs text-gray-500">(snake_case, immutable)</span></Label>
                  <Input
                    value={formData.doctype_name}
                    onChange={(e) => setFormData(p => ({ ...p, doctype_name: e.target.value.toLowerCase() }))}
                    placeholder="project_task"
                    disabled={!!editMode}
                    data-testid="doctype-name-input"
                  />
                </div>
                <div>
                  <Label>Label *</Label>
                  <Input
                    value={formData.label}
                    onChange={(e) => setFormData(p => ({ ...p, label: e.target.value }))}
                    placeholder="Project Task"
                    data-testid="doctype-label-input"
                  />
                </div>
                <div>
                  <Label>Module</Label>
                  <Input
                    value={formData.module}
                    onChange={(e) => setFormData(p => ({ ...p, module: e.target.value }))}
                    placeholder="Custom"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Checkbox
                    checked={formData.active}
                    onCheckedChange={(v) => setFormData(p => ({ ...p, active: !!v }))}
                  />
                  <Label>Active</Label>
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>

              {/* Naming */}
              <div className="border rounded-md p-3 bg-gray-50">
                <div className="font-medium mb-2 text-sm text-gray-700">Naming Rule</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Rule</Label>
                    <Select
                      value={formData.naming_rule}
                      onValueChange={(v) => setFormData(p => ({ ...p, naming_rule: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="autoincrement">Auto-increment (PREFIX-00001)</SelectItem>
                        <SelectItem value="field">By field value</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.naming_rule === 'autoincrement' ? (
                    <div>
                      <Label>Prefix</Label>
                      <Input
                        value={formData.naming_prefix}
                        onChange={(e) => setFormData(p => ({ ...p, naming_prefix: e.target.value.toUpperCase() }))}
                        placeholder="TASK"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label>Field Name</Label>
                      <Input
                        value={formData.naming_field}
                        onChange={(e) => setFormData(p => ({ ...p, naming_field: e.target.value.toLowerCase() }))}
                        placeholder="code"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Fields */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-sm text-gray-700">Fields</div>
                  <Button size="sm" variant="outline" onClick={addField}>
                    <Plus className="w-3 h-3 mr-1" /> Add Field
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.fields.map((f, idx) => (
                    <div key={f._uid} className="border rounded p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">
                          Field #{idx + 1}
                        </span>
                        {formData.fields.length > 1 && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeField(f._uid)}
                            className="h-7 w-7 text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-xs">Fieldname *</Label>
                          <Input
                            value={f.fieldname}
                            onChange={(e) => handleFieldChange(f._uid, { fieldname: e.target.value.toLowerCase() })}
                            placeholder="title"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Label *</Label>
                          <Input
                            value={f.label}
                            onChange={(e) => handleFieldChange(f._uid, { label: e.target.value })}
                            placeholder="Title"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Type</Label>
                          <Select
                            value={f.fieldtype}
                            onValueChange={(v) => handleFieldChange(f._uid, { fieldtype: v })}
                          >
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Default</Label>
                          <Input
                            value={f.default || ''}
                            onChange={(e) => handleFieldChange(f._uid, { default: e.target.value })}
                            className="h-8"
                          />
                        </div>
                        {f.fieldtype === 'select' && (
                          <div className="col-span-4">
                            <Label className="text-xs">Options (comma-separated) *</Label>
                            <Input
                              value={f.options}
                              onChange={(e) => handleFieldChange(f._uid, { options: e.target.value })}
                              placeholder="OPEN, IN_PROGRESS, DONE"
                              className="h-8"
                            />
                          </div>
                        )}
                        {f.fieldtype === 'link' && (
                          <div className="col-span-4">
                            <Label className="text-xs">Link Target DocType *</Label>
                            <Select
                              value={f.link_doctype || ''}
                              onValueChange={(v) => handleFieldChange(f._uid, { link_doctype: v })}
                            >
                              <SelectTrigger className="h-8" data-testid={`field-link-target-${idx}`}>
                                <SelectValue placeholder="Select target DocType" />
                              </SelectTrigger>
                              <SelectContent>
                                {definitions
                                  .filter(d => d.active && d.doctype_name !== formData.doctype_name)
                                  .map(d => (
                                    <SelectItem key={d.doctype_name} value={d.doctype_name}>
                                      {d.label} ({d.doctype_name})
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {definitions.filter(d => d.active && d.doctype_name !== formData.doctype_name).length === 0 && (
                              <p className="text-[11px] text-amber-600 mt-1">
                                Create another DocType first to link to it.
                              </p>
                            )}
                          </div>
                        )}
                        <div className="col-span-4 flex items-center gap-4 pt-1">
                          <label className="flex items-center gap-1 text-xs">
                            <Checkbox
                              checked={f.required}
                              onCheckedChange={(v) => handleFieldChange(f._uid, { required: !!v })}
                            />
                            Required
                          </label>
                          <label className="flex items-center gap-1 text-xs">
                            <Checkbox
                              checked={f.unique}
                              onCheckedChange={(v) => handleFieldChange(f._uid, { unique: !!v })}
                            />
                            Unique
                          </label>
                          <label className="flex items-center gap-1 text-xs">
                            <Checkbox
                              checked={f.in_list_view}
                              onCheckedChange={(v) => handleFieldChange(f._uid, { in_list_view: !!v })}
                            />
                            Show in list view
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-stone-800 hover:bg-stone-700"
                data-testid="save-doctype-btn"
              >
                {saving ? 'Saving…' : (editMode ? 'Save Changes' : 'Create DocType')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DocTypeBuilder;
